import { useMemo, useState } from "react";
import { IconChevronDown, IconVariable } from "@tabler/icons-react";
import type { TemplateVariable } from "../api/queries/emailTemplateQueries";

const CATEGORY_LABELS: Record<string, string> = {
  customer: "Customer",
  invoice: "Invoice",
  sender: "Sender",
};

interface EmailTemplateVariablePickerProps {
  variables: TemplateVariable[];
  onInsert: (token: string) => void;
  disabled?: boolean;
  scopeFilter?: Set<string>;
  label?: string;
  description?: string;
}

export function EmailTemplateVariablePicker({
  variables,
  onInsert,
  disabled = false,
  scopeFilter,
  label = "Insert variable",
  description = "Click a variable to insert it into the focused subject or body field.",
}: EmailTemplateVariablePickerProps) {
  const [open, setOpen] = useState(true);

  const grouped = useMemo(() => {
    const map = new Map<string, TemplateVariable[]>();
    for (const variable of variables) {
      if (scopeFilter && !scopeFilter.has(variable.scope)) {
        continue;
      }
      const list = map.get(variable.category) ?? [];
      list.push(variable);
      map.set(variable.category, list);
    }
    return map;
  }, [variables, scopeFilter]);

  if (variables.length === 0) {
    return null;
  }

  const hasVisibleVariables = [...grouped.values()].some(
    (items) => items.length > 0,
  );
  if (!hasVisibleVariables) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-[#FAFAFA] p-3">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
          <IconVariable size={16} className="text-[#4B7F73]" />
          {label}
        </span>
        <IconChevronDown
          size={16}
          className={`text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">{description}</p>
          {[...grouped.entries()].map(([category, items]) => (
            <div key={category}>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                {CATEGORY_LABELS[category] ?? category}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {items.map((variable) => (
                  <button
                    key={variable.key}
                    type="button"
                    disabled={disabled}
                    title={`${variable.description} — e.g. ${variable.example}`}
                    onClick={() => onInsert(`{${variable.key}}`)}
                    className="rounded-full bg-[#EEF4F2] px-2.5 py-1 text-xs font-medium text-[#4B7F73] transition-colors hover:bg-[#4B7F73] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {`{${variable.key}}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
